// 滚动幻灯片组件 - 重构版本
class ScrollSlideshowSection {
	constructor(element) {
		this.element = element;
		this.currentProgress = 0;
		this.totalCards = parseInt(element.dataset.totalCards) || 0;
		this.containerMarginX = parseInt(element.dataset.containerMarginX) || 80;
		this.isInView = false;
		this.animationFrame = null;
		this.isMobile = false;
		this.currentSlide = 0;

		this.init();
	}

	init() {
		this.checkMobile();
		this.setupEventListeners();

		if (this.isMobile) {
			this.initMobileSlider();
		} else {
			this.initDesktopScrollAnimation();
		}
	}

	checkMobile() {
		this.isMobile = window.innerWidth < 1024; // lg断点
	}

	setupEventListeners() {
		// 窗口大小变化监听
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.checkMobile();

			if (wasMobile !== this.isMobile) {
				// 重新初始化
				this.cleanup();
				this.init();
			}
		});

		// 视频播放控制
		this.setupVideoControls();
	}

	setupVideoControls() {
		const videos = this.element.querySelectorAll('video');
		videos.forEach((video) => {
			// 确保视频在视口内时播放
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							video.play().catch((e) => console.log('视频播放失败:', e));
						} else {
							video.pause();
						}
					});
				},
				{threshold: 0.5}
			);

			observer.observe(video);
		});
	}

	// 移动端滑动器初始化
	initMobileSlider() {
		const slider = this.element.querySelector('#mobileSlider');
		const dots = this.element.querySelectorAll('.pagination-dot');

		if (!slider || !dots.length) return;

		// 确保卡片高度一致
		this.equalizeCardHeights();

		// 滑动事件处理
		this.setupMobileSliderEvents(slider, dots);

		// 触摸支持
		this.setupTouchSupport(slider);
	}

	setupMobileSliderEvents(slider, dots) {
		let isScrolling = false;

		slider.addEventListener('scroll', () => {
			if (isScrolling) return;

			isScrolling = true;
			requestAnimationFrame(() => {
				const scrollLeft = slider.scrollLeft;
				const containerWidth = slider.offsetWidth;
				const slideWidth = containerWidth * 0.96;
				const newSlide = Math.round(scrollLeft / slideWidth);

				if (newSlide !== this.currentSlide) {
					this.currentSlide = newSlide;
					this.updatePaginationDots();
				}

				isScrolling = false;
			});
		});

		// 导航点点击
		dots.forEach((dot, index) => {
			dot.addEventListener('click', () => {
				this.currentSlide = index;
				this.scrollToSlide(index);
				this.updatePaginationDots();
			});
		});
	}

	setupTouchSupport(slider) {
		let startX = 0;
		let startY = 0;
		let isDown = false;
		let isHorizontalScroll = false;

		slider.addEventListener('touchstart', (e) => {
			isDown = true;
			startX = e.touches[0].clientX;
			startY = e.touches[0].clientY;
			isHorizontalScroll = false;
		});

		slider.addEventListener('touchmove', (e) => {
			if (!isDown) return;

			const currentX = e.touches[0].clientX;
			const currentY = e.touches[0].clientY;
			const diffX = startX - currentX;
			const diffY = startY - currentY;

			if (!isHorizontalScroll) {
				isHorizontalScroll = Math.abs(diffX) > Math.abs(diffY);
			}

			if (isHorizontalScroll) {
				e.preventDefault();
			}
		});

		slider.addEventListener('touchend', (e) => {
			if (!isDown) return;
			isDown = false;

			const endX = e.changedTouches[0].clientX;
			const diffX = startX - endX;
			const threshold = 50;

			if (isHorizontalScroll && Math.abs(diffX) > threshold) {
				if (diffX > 0 && this.currentSlide < this.totalCards - 1) {
					this.currentSlide++;
				} else if (diffX < 0 && this.currentSlide > 0) {
					this.currentSlide--;
				}
				this.scrollToSlide(this.currentSlide);
				this.updatePaginationDots();
			}
		});
	}

	scrollToSlide(index) {
		const slider = this.element.querySelector('#mobileSlider');
		if (!slider) return;

		const containerWidth = slider.offsetWidth;
		const slideWidth = containerWidth * 0.96;
		slider.scrollTo({
			left: slideWidth * index,
			behavior: 'smooth'
		});
	}

	updatePaginationDots() {
		const dots = this.element.querySelectorAll('.pagination-dot');
		dots.forEach((dot, index) => {
			if (index === this.currentSlide) {
				dot.classList.add('active');
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.classList.add('bg-gray-600');
			}
		});
	}

	equalizeCardHeights() {
		const cards = this.element.querySelectorAll('.feature-card');
		if (!cards.length) return;

		// 重置高度
		cards.forEach((card) => {
			card.style.height = 'auto';
		});

		// 计算最大高度
		let maxHeight = 0;
		cards.forEach((card) => {
			const height = card.offsetHeight;
			maxHeight = Math.max(maxHeight, height);
		});

		// 应用最大高度
		cards.forEach((card) => {
			card.style.height = `${maxHeight + 12}px`;
		});
	}

	// 桌面端滚动动画初始化
	initDesktopScrollAnimation() {
		this.setupScrollListener();
		this.initializeDesktopCards();
	}

	setupScrollListener() {
		this.handleScroll = this.handleScrollThrottled.bind(this);
		window.addEventListener('scroll', this.handleScroll, {passive: true});
		this.checkViewport();
	}

	handleScrollThrottled() {
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
		}

		this.animationFrame = requestAnimationFrame(() => {
			this.checkViewport();
		});
	}

	checkViewport() {
		const desktopLayout = this.element.querySelector('.desktop-layout');
		if (!desktopLayout) return;

		const rect = desktopLayout.getBoundingClientRect();
		const windowHeight = window.innerHeight;

		this.isInView = rect.top <= windowHeight && rect.bottom >= 0;

		if (!this.isInView) {
			this.resetToInitialState();
			return;
		}

		const containerHeight = rect.height;
		const scrollProgress = Math.max(
			0,
			Math.min(1, -rect.top / (containerHeight - windowHeight))
		);

		this.updateDesktopAnimation(scrollProgress);
	}

	updateDesktopAnimation(progress) {
		this.currentProgress = progress;

		// 阶段定义
		const stages = {
			intro: {start: 0, end: 0.1},
			stable: {start: 0.1, end: 0.15},
			cards: {start: 0.15, end: 0.9},
			outro: {start: 0.9, end: 1.0}
		};

		this.updateLeftPanel(progress, stages);
		this.updateDesktopCards(progress, stages);
		this.updateScrollIndicator(progress);
	}

	updateLeftPanel(progress, stages) {
		const leftPanel = this.element.querySelector('.left-panel');
		if (!leftPanel) return;

		if (progress >= stages.intro.start && progress <= stages.outro.start) {
			if (progress <= stages.intro.end) {
				const introProgress =
					(progress - stages.intro.start) /
					(stages.intro.end - stages.intro.start);
				leftPanel.style.opacity = introProgress;
				leftPanel.style.transform = `translateX(${-50 + 50 * introProgress}px)`;
			} else if (progress <= stages.outro.start) {
				leftPanel.style.opacity = '1';
				leftPanel.style.transform = 'translateX(0)';
			}
		} else if (progress > stages.outro.start) {
			const outroProgress =
				(progress - stages.outro.start) /
				(stages.outro.end - stages.outro.start);
			leftPanel.style.opacity = 1 - outroProgress;
			leftPanel.style.transform = `translateX(${-50 * outroProgress}px)`;
		}
	}

	updateDesktopCards(progress, stages) {
		if (progress < stages.cards.start || progress > stages.outro.start) {
			this.hideAllDesktopCards();
			return;
		}

		const cardsRange = stages.cards.end - stages.cards.start;
		const cardProgress = (progress - stages.cards.start) / cardsRange;
		const cardDuration = 1 / this.totalCards;
		const currentCardIndex = Math.floor(cardProgress / cardDuration);
		const localProgress = (cardProgress % cardDuration) / cardDuration;

		this.showDesktopCard(currentCardIndex, localProgress);

		if (progress > stages.outro.start) {
			const outroProgress =
				(progress - stages.outro.start) /
				(stages.outro.end - stages.outro.start);
			this.fadeOutAllDesktopCards(outroProgress);
		}
	}

	showDesktopCard(cardIndex, localProgress) {
		const cards = this.element.querySelectorAll('.slide-card');

		// 隐藏其他卡片
		cards.forEach((card, index) => {
			if (index !== cardIndex) {
				card.style.opacity = '0';
				card.style.transform = 'translateY(50px) scale(0.95)';
			}
		});

		// 显示当前卡片
		if (cards[cardIndex]) {
			const card = cards[cardIndex];

			if (localProgress <= 0.2) {
				const fadeInProgress = localProgress / 0.2;
				card.style.opacity = fadeInProgress;
				card.style.transform = `translateY(${
					50 - 50 * fadeInProgress
				}px) scale(${0.95 + 0.05 * fadeInProgress})`;
			} else if (localProgress <= 0.8) {
				card.style.opacity = '1';
				card.style.transform = 'translateY(0) scale(1)';
			} else {
				const fadeOutProgress = (localProgress - 0.8) / 0.2;
				card.style.opacity = 1 - fadeOutProgress;
				card.style.transform = `translateY(${-50 * fadeOutProgress}px) scale(${
					1 - 0.05 * fadeOutProgress
				})`;
			}
		}
	}

	hideAllDesktopCards() {
		const cards = this.element.querySelectorAll('.slide-card');
		cards.forEach((card) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(50px) scale(0.95)';
		});
	}

	fadeOutAllDesktopCards(outroProgress) {
		const cards = this.element.querySelectorAll('.slide-card');
		cards.forEach((card) => {
			const currentOpacity = parseFloat(card.style.opacity) || 0;
			card.style.opacity = currentOpacity * (1 - outroProgress);
		});
	}

	resetToInitialState() {
		const leftPanel = this.element.querySelector('.left-panel');
		if (leftPanel) {
			leftPanel.style.opacity = '0';
			leftPanel.style.transform = 'translateX(-50px)';
		}

		this.hideAllDesktopCards();
	}

	updateScrollIndicator(progress) {
		const indicator = this.element.querySelector('.scroll-indicator');
		const progressBar = this.element.querySelector('.scroll-progress');

		if (indicator && progressBar) {
			if (this.isInView && progress > 0 && progress < 1) {
				indicator.style.opacity = '1';
				progressBar.style.height = `${progress * 100}%`;
			} else {
				indicator.style.opacity = '0';
			}
		}
	}

	initializeDesktopCards() {
		const cards = this.element.querySelectorAll('.slide-card');
		cards.forEach((card) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(50px) scale(0.95)';
		});
	}

	cleanup() {
		if (this.handleScroll) {
			window.removeEventListener('scroll', this.handleScroll);
		}
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
		}
	}
}

// 初始化所有滚动幻灯片组件
document.addEventListener('DOMContentLoaded', () => {
	const scrollSlideshowSections = document.querySelectorAll(
		'.scroll-slideshow-section'
	);
	scrollSlideshowSections.forEach((section) => {
		new ScrollSlideshowSection(section);
	});
});

// 导出组件类
window.ScrollSlideshowSection = ScrollSlideshowSection;
